import { create } from 'zustand';
import type { DefenderType, UserDefender } from '@types';

interface DefenderState {
  // Base defender types from API
  defenderTypes: DefenderType[];
  // User's defender progress
  userDefenders: UserDefender[];
  // Loading states
  isLoadingTypes: boolean;
  isLoadingUserDefenders: boolean;

  // Actions
  setDefenderTypes: (types: DefenderType[]) => void;
  setUserDefenders: (defenders: UserDefender[]) => void;
  setLoadingTypes: (loading: boolean) => void;
  setLoadingUserDefenders: (loading: boolean) => void;

  // Helpers
  getDefenderById: (defenderId: number) => DefenderType | undefined;
  getUserDefenderById: (defenderId: number) => UserDefender | undefined;
  getDefenderDataById: (defenderId: number) => {
    baseStats: DefenderType;
    userProgress: UserDefender;
    currentDamage: number;
    nextLevelDamage: number;
  } | null;
}

export const useDefenderStore = create<DefenderState>((set, get) => ({
  defenderTypes: [],
  userDefenders: [],
  isLoadingTypes: false,
  isLoadingUserDefenders: false,

  setDefenderTypes: (types) => set({ defenderTypes: types }),
  setUserDefenders: (defenders) => set({ userDefenders: defenders }),
  setLoadingTypes: (loading) => set({ isLoadingTypes: loading }),
  setLoadingUserDefenders: (loading) => set({ isLoadingUserDefenders: loading }),

  getDefenderById: (defenderId) => {
    const { defenderTypes } = get();
    return defenderTypes.find((type) => type.id === defenderId);
  },

  getUserDefenderById: (defenderId) => {
    const { userDefenders } = get();
    return userDefenders.find((defender) => defender.defenderId === defenderId);
  },

  getDefenderDataById: (defenderId) => {
    const { defenderTypes, userDefenders } = get();
    const baseStats = defenderTypes.find((type) => type.id === defenderId);
    const userProgress = userDefenders.find(
      (defender) => defender.defenderId === defenderId
    );

    if (!baseStats || !userProgress) return null;

    // Calculate current damage based on level
    const currentDamage =
      baseStats.damage + baseStats.damagePerLevel * (userProgress.level - 1);

    // Calculate next level damage
    const nextLevelDamage =
      baseStats.damage + baseStats.damagePerLevel * userProgress.level;

    return {
      baseStats,
      userProgress,
      currentDamage,
      nextLevelDamage,
    };
  },
}));
